import {type ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {NavigationIcon, useAccountSidebar} from './signed-in-layout';
import {Loading, dateTime} from './components';

import './workspace-ui.css';
export type WorkspacePage = 'workspace' | 'team' | 'tasks' | 'files' | 'settings';
export interface WorkChatRow {
  id: string;
  title: string;
  preview: string | null;
  updatedAt: string | null;
}

function chatTime(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) throw new Error(`Invalid work chat date: ${value}`);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return new Intl.DateTimeFormat(undefined, {hour: 'numeric', minute: '2-digit'}).format(date);
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return new Intl.DateTimeFormat(undefined, {month: 'short', day: 'numeric'}).format(date);
}

export function WorkspaceSidebar({chats, selectedChatId, onSelect, onCreate, busy = false, createDisabled = false,
  loading = false, status, onNavigate}: {
  chats: readonly WorkChatRow[]; selectedChatId: string | null; onSelect: (id: string) => void;
  onCreate: () => Promise<boolean>; busy?: boolean; createDisabled?: boolean; loading?: boolean;
  status?: ReactNode;
  onNavigate: () => void;
}) {
  return <div className="workspace-sidebar">
    <div className="workspace-sidebar-top"><div className="workspace-sidebar-heading"><h2 tabIndex={-1}>Chats</h2>
      </div>
      <button type="button" className="workspace-new-chat" disabled={busy || createDisabled}
        onClick={() => void onCreate().then(created => {if (created) onNavigate();})}><NavigationIcon name="plus"/>New chat</button>
    </div>
    <div className="workspace-sidebar-chats">{status}{loading && chats.length === 0 && <Loading/>}
      <nav aria-label="Saved work chats"><ul className="workspace-chat-list">{chats.map(row => <li key={row.id}><button type="button"
          className={`workspace-chat-row${selectedChatId === row.id ? ' is-current' : ''}`} aria-current={selectedChatId === row.id ? 'page' : undefined}
          onClick={() => {onSelect(row.id); onNavigate();}} disabled={busy}>
          <span className="workspace-chat-title" title={row.title}>{row.title}</span>
          {row.updatedAt && <time dateTime={row.updatedAt} title={dateTime(row.updatedAt)}>{chatTime(row.updatedAt)}</time>}
        </button></li>)}</ul></nav>
      {!loading && chats.length === 0 && <p className="workspace-sidebar-empty">Your chats will appear here.</p>}
    </div>
  </div>;
}

export function WorkspaceShell({sidebar, conversation, children, conversationInMain = false, className = ''}: {
  sidebar: (controls: {onNavigate: () => void}) => ReactNode;
  conversation?: ReactNode; children: ReactNode; conversationInMain?: boolean; className?: string;
}) {
  const navigation = useAccountSidebar();
  return <div className={`workspace-shell shared-navigation ${conversation ? (conversationInMain ? 'has-main-conversation ' : 'has-conversation ') : ''}${className}`}>
    {navigation.target && createPortal(sidebar({onNavigate: navigation.onNavigate}), navigation.target)}
    <div className="workspace-main"><div className="workspace-body">{children}</div>{conversationInMain && conversation}</div>
    {!conversationInMain && conversation}
  </div>;
}
